import BigNumber from 'bignumber.js'

import { NativeAssetInfo } from '@/config/chain/types'
import { AaveUserReserve, AaveUserSummary } from '@/domain/market-info/aave-data-layer/query'
import {
  MarketInfo,
  Reserve,
  UserConfiguration,
  UserPosition,
  UserPositionSummary,
} from '@/domain/market-info/marketInfo'
import { CheckedAddress } from '@/domain/types/CheckedAddress'
import { NormalizedUnitNumber, Percentage } from '@/domain/types/NumericValues'
import { Token } from '@/domain/types/Token'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { zeroAddress } from 'viem'
import { hyperEVM } from '@/config/chain/constants'

export const testAddresses = {
  alice: createDummyAddress('a11ce'),
  bob: createDummyAddress('b0b'),
  token: createDummyAddress('7041311'), // if you squint, it looks like "token"
  token2: createDummyAddress('70413112'),
  token3: createDummyAddress('70413113'),
  token4: createDummyAddress('70413114'),
}

function createDummyAddress(prefix: string): CheckedAddress {
  const address = prefix + '0'.repeat(40 - prefix.length)

  return CheckedAddress(`0x${address}`)
}

export function getMockAaveUserReserve(overrides: Partial<AaveUserReserve> = {}): AaveUserReserve {
  return {
    underlyingAsset: '0x702aB9B6556020F5c7AC814162Cb641d64291144',
    name: 'Wrapped HYPE',
    symbol: 'WHYPE',
    decimals: 18,
    baseLTVasCollateral: '6850',
    reserveLiquidationThreshold: '7950',
    reserveLiquidationBonus: '10700',
    reserveFactor: '0.15',
    usageAsCollateralEnabled: true,
    borrowingEnabled: true,
    stableBorrowRateEnabled: false,
    isActive: true,
    isFrozen: false,
    eModeCategoryId: 1,
    liquidityIndex: '1000040121636005118530217371',
    variableBorrowIndex: '1002639258351271728705309220',
    liquidityRate: '2394836129343291714447',
    variableBorrowRate: '2608029980148289558043989',
    stableBorrowRate: '45000000000000000000000000',
    lastUpdateTimestamp: 1707226247,
    aTokenAddress: '0x0D97A8F973975191F868Cfa76C0b0aeDD1634B17',
    stableDebtTokenAddress: '0x7D5789F872b2C386d47e5974752cBA795deCd6d9',
    variableDebtTokenAddress: '0x4eDF296e33e7f00DD5f14fC8640e1b70f3672B4C',
    interestRateStrategyAddress: '0x7CD29c49B3862C2445D4A6A5e0C9fD27f3Ac7E28',
    availableLiquidity: '442037025543508881719022',
    totalPrincipalStableDebt: '0',
    averageStableRate: '0',
    stableDebtLastUpdateTimestamp: 0,
    totalScaledVariableDebt: '476.790573622181008097',
    priceInMarketReferenceCurrency: '262935513695',
    priceOracle: '0x3afe972eCdaBC2050490b2b97e23641b66513b7A',
    variableRateSlope1: '45000000000000000000000000',
    variableRateSlope2: '800000000000000000000000000',
    stableRateSlope1: '0',
    stableRateSlope2: '0',
    baseStableBorrowRate: '45000000000000000000000000',
    baseVariableBorrowRate: '2500000000000000000000000',
    optimalUsageRatio: '450000000000000000000000000',
    isPaused: false,
    isSiloedBorrowing: false,
    accruedToTreasury: '252777594703129452',
    unbacked: '0',
    isolationModeTotalDebt: '0',
    flashLoanEnabled: true,
    debtCeiling: '0',
    debtCeilingDecimals: 2,
    borrowCap: '3000',
    supplyCap: '800000',
    eModeLtv: 9000,
    eModeLiquidationThreshold: 9300,
    eModeLiquidationBonus: 10100,
    eModePriceSource: '0x0000000000000000000000000000000000000000',
    eModeLabel: 'ETH',
    borrowableInIsolation: false,
    id: '1-0x7f39C581F595B53c5cb19bD0b3f8dA6c935E2Ca0-0x02C3eA4e34C0cBd694D2adFa2c690EECbC1793eE',
    totalDebt: '478.059161298460858355',
    totalStableDebt: '0',
    totalVariableDebt: '478.059161298460858355',
    totalLiquidity: '442515.084704807342577377',
    borrowUsageRatio: '0.00108032285863738235',
    supplyUsageRatio: '0.00108032285863738235',
    formattedReserveLiquidationBonus: '0.07',
    supplyAPY: '0.00000239483899696551',
    variableBorrowAPY: '0.00261143384871612347',
    stableBorrowAPY: '0.04602785987513300031',
    formattedAvailableLiquidity: '442037.025543508881719022',
    unborrowedLiquidity: '442037.025543508881719022',
    formattedBaseLTVasCollateral: '0.685',
    supplyAPR: '0.00000239483612934329',
    variableBorrowAPR: '0.00260802998014828956',
    stableBorrowAPR: '0.045',
    formattedReserveLiquidationThreshold: '0.795',
    formattedEModeLiquidationBonus: '0.01',
    formattedEModeLiquidationThreshold: '0.93',
    formattedEModeLtv: '0.9',
    debtCeilingUSD: '0',
    isolationModeTotalDebtUSD: '0',
    availableDebtCeilingUSD: '0',
    isIsolated: false,
    totalLiquidityUSD: '1163529311.14644956056590466781',
    availableLiquidityUSD: '6631078.09932388330995314507',
    totalDebtUSD: '1256987.31152611669004422558',
    totalVariableDebtUSD: '1256987.31152611669004422558',
    totalStableDebtUSD: '0',
    formattedPriceInMarketReferenceCurrency: '2629.35513695',
    priceInUSD: '2629.35513695',
    borrowCapUSD: '7888065.41085',
    supplyCapUSD: '2103484109.56',
    unbackedUSD: '0',
    aIncentivesData: [],
    vIncentivesData: [],
    sIncentivesData: [],
    ...overrides,
  }
}

export function getMockReserve(overrides: Partial<Reserve> = {}): Reserve {
  const priceInUsd = 20
  const token = new Token({
    address: testAddresses.token,
    symbol: TokenSymbol('WHYPE'),
    name: 'Wrapped HYPE',
    decimals: 18,
    unitPriceUsd: priceInUsd.toString(),
  })

  return {
    token,

    aToken: token.clone({ symbol: TokenSymbol('hyWHYPE') }),
    variableDebtTokenAddress: CheckedAddress('0x4eDF296e33e7f00DD5f14fC8640e1b70f3672B4C'),

    status: 'active',

    supplyAvailabilityStatus: 'yes',
    collateralEligibilityStatus: 'yes',
    borrowEligibilityStatus: 'yes',

    isIsolated: false,
    eModeCategory: undefined,

    availableLiquidity: NormalizedUnitNumber(100),
    availableLiquidityUSD: NormalizedUnitNumber(100 * priceInUsd),
    unborrowedLiquidity: NormalizedUnitNumber(1000),
    supplyCap: undefined,
    totalLiquidity: NormalizedUnitNumber(200),
    totalLiquidityUSD: NormalizedUnitNumber(200 * priceInUsd),
    totalDebt: NormalizedUnitNumber(100),
    totalDebtUSD: NormalizedUnitNumber(100 * priceInUsd),
    totalVariableDebt: NormalizedUnitNumber(100),
    totalVariableDebtUSD: NormalizedUnitNumber(100 * priceInUsd),
    isolationModeTotalDebt: NormalizedUnitNumber(0),
    debtCeiling: NormalizedUnitNumber(200),
    supplyAPY: Percentage(0.05),
    maxLtv: Percentage(0.8),
    liquidationThreshold: Percentage(0.8),
    liquidationBonus: Percentage(0.05),
    aTokenBalance: NormalizedUnitNumber(0),

    lastUpdateTimestamp: 0,

    variableBorrowIndex: new BigNumber(0),
    variableBorrowRate: new BigNumber(0),
    liquidityIndex: new BigNumber(0),
    liquidityRate: new BigNumber(0),
    variableRateSlope1: new BigNumber(0),
    variableRateSlope2: new BigNumber(0),
    baseVariableBorrowRate: new BigNumber(0),
    optimalUtilizationRate: Percentage(0.8),
    utilizationRate: Percentage(0.5),
    reserveFactor: Percentage(0.05),
    isBorrowableInIsolation: false,

    variableBorrowApy: Percentage(0.05),

    priceInUSD: new BigNumber(priceInUsd),
    priceOracle: CheckedAddress(zeroAddress),

    usageAsCollateralEnabled: true,
    usageAsCollateralEnabledOnUser: true,
    isSiloedBorrowing: false,

    incentives: { borrow: [], deposit: [] },
    ...overrides,
  }
}

export function getMockUserPosition(overrides?: Partial<UserPosition>): UserPosition {
  return {
    reserve: getMockReserve(),
    scaledATokenBalance: NormalizedUnitNumber(0),
    scaledVariableDebt: NormalizedUnitNumber(0),
    borrowBalance: NormalizedUnitNumber(0),
    collateralBalance: NormalizedUnitNumber(0),
    ...overrides,
  }
}

export function getMockUserPositionSummary(overrides?: Partial<UserPositionSummary>): UserPositionSummary {
  return {
    loanToValue: Percentage(0),
    maxLoanToValue: Percentage(0),
    healthFactor: new BigNumber(0),
    availableBorrowsUSD: NormalizedUnitNumber(0),
    totalBorrowsUSD: NormalizedUnitNumber(0),
    currentLiquidationThreshold: Percentage(0),
    totalCollateralUSD: NormalizedUnitNumber(0),
    totalLiquidityUSD: NormalizedUnitNumber(0),
    ...overrides,
  }
}

export function getMockToken(overrides?: Partial<ConstructorParameters<typeof Token>[0]>): Token {
  return new Token({
    address: testAddresses.token,
    symbol: TokenSymbol('TKN'),
    name: 'Token',
    decimals: 18,
    unitPriceUsd: '2000',
    ...overrides,
  })
}

export function getMockMarketInfo(
  reserves: Reserve[] = [daiLikeReserve, wethLikeReserve],
  userPosition?: UserPosition[],
  userPositionSummary?: UserPositionSummary,
  userConfiguration?: UserConfiguration,
  chainId?: number,
  nativeAssetInfo?: NativeAssetInfo,
): MarketInfo {
  return new MarketInfo(
    reserves,
    userPosition ?? reserves.map((r) => getMockUserPosition({ reserve: r })),
    userPositionSummary ?? getMockUserPositionSummary(),
    userConfiguration ?? {
      eModeState: { enabled: false },
      isolationModeState: { enabled: false },
      siloBorrowingState: { enabled: false },
    },
    {},
    new Date('2024-06-04T10:21:19Z').getTime() / 1000,
    chainId ?? hyperEVM.id,
    [], // no user rewards
    nativeAssetInfo ?? {
      nativeAssetName: 'HYPE',
      wrappedNativeAssetSymbol: TokenSymbol('WHYPE'),
      wrappedNativeAssetAddress: CheckedAddress('0x4eDF296e33e7f00DD5f14fC8640e1b70f3672B4C'),
      nativeAssetSymbol: TokenSymbol('HYPE'),
      minRemainingNativeAssetBalance: NormalizedUnitNumber(0.001),
    },
    {
      netSupplyApy: Percentage(0.05),
      netBorrowApy: Percentage(0.05),
      totalNetApy: Percentage(0.05),
    },
    {
      availableToBorrow: NormalizedUnitNumber(50),
      borrowLimit: NormalizedUnitNumber(100),
      currentBorrowed: NormalizedUnitNumber(50),
      utilizationRate: Percentage(0.5),
      totalSupply: NormalizedUnitNumber(50),
    },
  )
}

export const daiLikeReserve = getMockReserve({
  token: new Token({
    symbol: TokenSymbol('DAI'),
    name: 'Dai Stablecoin',
    decimals: 18,
    address: CheckedAddress('0x6B175474E89094C44Da98b954EedeAC495271d0F'),
    unitPriceUsd: '1',
  }),
  status: 'active',
  supplyAvailabilityStatus: 'yes',
  collateralEligibilityStatus: 'no',
  borrowEligibilityStatus: 'yes',
  isIsolated: false,
  isBorrowableInIsolation: true,
  eModeCategory: undefined,
  isSiloedBorrowing: false,
  availableLiquidity: NormalizedUnitNumber('24662247.809387867477416918'),
  availableLiquidityUSD: NormalizedUnitNumber('24662247.809387867477416918'),
  totalLiquidity: NormalizedUnitNumber('1022367834.197032800423989866'),
  totalLiquidityUSD: NormalizedUnitNumber('1022367834.197032800423989866'),
  totalDebt: NormalizedUnitNumber('997705586.387644932946572948'),
  totalDebtUSD: NormalizedUnitNumber('997705586.387644932946572948'),
  totalVariableDebt: NormalizedUnitNumber('997705586.387644932946572948'),
  totalVariableDebtUSD: NormalizedUnitNumber('997705586.387644932946572948'),
  isolationModeTotalDebt: NormalizedUnitNumber('0'),
  debtCeiling: NormalizedUnitNumber('0'),
  supplyAPY: Percentage('0.06299360148523566431'),
  maxLtv: Percentage('0'),
  variableBorrowApy: Percentage('0.06459999999999999996'),
  lastUpdateTimestamp: 1708334846,
  variableBorrowIndex: new BigNumber('1.035736103938278845101422738e+27'),
  variableBorrowRate: new BigNumber('6.2599141818649791361008e+25'),
  liquidityIndex: new BigNumber('1.028914737353923264312907136e+27'),
  liquidityRate: new BigNumber('6.1089080101931682768749404e+25'),
  priceInUSD: NormalizedUnitNumber('1'),
  usageAsCollateralEnabled: false,
  usageAsCollateralEnabledOnUser: false,
  incentives: { deposit: [], borrow: [] },
})

type FormattedAaveUserReservesData = AaveUserSummary['userReservesData'][0]

export function getMockAaveFormattedUserSummaryReserve(
  overrides: Partial<FormattedAaveUserReservesData['reserve']> = {},
): FormattedAaveUserReservesData['reserve'] {
  return {
    underlyingAsset: '0x4B85aCF84b2593D67f6593D18504dBb3A337D3D8',
    name: 'Solv BTC',
    symbol: 'SolvBTC',
    decimals: 18,
    baseLTVasCollateral: '7000',
    reserveLiquidationThreshold: '7500',
    reserveLiquidationBonus: '10750',
    reserveFactor: '0',
    usageAsCollateralEnabled: true,
    borrowingEnabled: true,
    stableBorrowRateEnabled: false,
    isActive: true,
    isFrozen: false,
    liquidityIndex: '1000001142660161156360586911',
    variableBorrowIndex: '1000009390643113370787351582',
    liquidityRate: '349840191469968457350779',
    variableBorrowRate: '4182344984993278010582251',
    stableBorrowRate: '52091172492496639005291125',
    lastUpdateTimestamp: 1738335220,
    aTokenAddress: '0x38D92fA1Bc6D946818427aC27F390fE5789EDC8F',
    stableDebtTokenAddress: '0x52Dbb47b8aaC70cBf8B05958d6E5d020A28c777c',
    variableDebtTokenAddress: '0xf5e5aB271Cf859eB7a7c0F2a079E188F28297485',
    interestRateStrategyAddress: '0x7CD29c49B3862C2445D4A6A5e0C9fD27f3Ac7E28',
    availableLiquidity: '1142421938227517388',
    totalPrincipalStableDebt: '0',
    averageStableRate: '0',
    stableDebtLastUpdateTimestamp: 0,
    totalScaledVariableDebt: '0.104282023914815228',
    priceInMarketReferenceCurrency: '10434526308105',
    priceOracle: '0x85C4F855Bc0609D2584405819EdAEa3aDAbfE97D',
    variableRateSlope1: '40000000000000000000000000',
    variableRateSlope2: '750000000000000000000000000',
    stableRateSlope1: '20000000000000000000000000',
    stableRateSlope2: '750000000000000000000000000',
    baseStableBorrowRate: '50000000000000000000000000',
    baseVariableBorrowRate: '0',
    optimalUsageRatio: '800000000000000000000000000',
    isPaused: false,
    isSiloedBorrowing: false,
    accruedToTreasury: '0',
    unbacked: '0',
    isolationModeTotalDebt: '0',
    flashLoanEnabled: false,
    debtCeiling: '0',
    debtCeilingDecimals: 2,
    eModeCategoryId: 0,
    borrowCap: '0',
    supplyCap: '0',
    eModeLtv: 0,
    eModeLiquidationThreshold: 0,
    eModeLiquidationBonus: 0,
    eModePriceSource: '0x0000000000000000000000000000000000000000',
    eModeLabel: '',
    borrowableInIsolation: false,
    id: '999-0x4B85aCF84b2593D67f6593D18504dBb3A337D3D8-0x8c52538C6c94a80fC907279A32d3aA9D51C9f2d3',
    totalDebt: '0.104283010326440714',
    totalStableDebt: '0',
    totalVariableDebt: '0.104283010326440714',
    totalLiquidity: '1.246704948553958102',
    borrowUsageRatio: '0.08364690494522993848',
    supplyUsageRatio: '0.08364690494522993848',
    formattedReserveLiquidationBonus: '0.075',
    formattedEModeLiquidationBonus: '-1',
    formattedEModeLiquidationThreshold: '0',
    formattedEModeLtv: '0',
    supplyAPY: '0',
    variableBorrowAPY: '0',
    stableBorrowAPY: '0',
    formattedAvailableLiquidity: '1.142421938227517388',
    unborrowedLiquidity: '1.142421938227517388',
    formattedBaseLTVasCollateral: '0.7',
    supplyAPR: '0',
    variableBorrowAPR: '0',
    stableBorrowAPR: '0',
    formattedReserveLiquidationThreshold: '0.75',
    debtCeilingUSD: '0',
    isolationModeTotalDebtUSD: '0',
    availableDebtCeilingUSD: '0',
    isIsolated: false,
    totalLiquidityUSD: '130087.75584130966392446913',
    availableLiquidityUSD: '119206.31769391335378127333',
    totalDebtUSD: '10881.4381473963101431958',
    totalVariableDebtUSD: '10881.4381473963101431958',
    totalStableDebtUSD: '0',
    formattedPriceInMarketReferenceCurrency: '104345.26308105',
    priceInUSD: '104345.26308105',
    borrowCapUSD: '0',
    supplyCapUSD: '0',
    unbackedUSD: '0',
    aIncentivesData: [],
    vIncentivesData: [],
    sIncentivesData: [],
    ...overrides,
  }
}

export function getMockUserReservesData(
  reserve: FormattedAaveUserReservesData['reserve'],
  overrides: Partial<FormattedAaveUserReservesData> = {},
): FormattedAaveUserReservesData {
  return {
    underlyingAsset: '0x4B85aCF84b2593D67f6593D18504dBb3A337D3D8',
    scaledATokenBalance: '0',
    usageAsCollateralEnabledOnUser: false,
    stableBorrowRate: '0',
    scaledVariableDebt: '0',
    principalStableDebt: '0',
    stableBorrowLastUpdateTimestamp: 0,
    underlyingBalance: '0',
    underlyingBalanceMarketReferenceCurrency: '0',
    underlyingBalanceUSD: '0',
    stableBorrows: '0',
    stableBorrowsMarketReferenceCurrency: '0',
    stableBorrowsUSD: '0',
    variableBorrows: '0',
    variableBorrowsMarketReferenceCurrency: '0',
    variableBorrowsUSD: '0',
    totalBorrows: '0',
    totalBorrowsMarketReferenceCurrency: '0',
    totalBorrowsUSD: '0',
    stableBorrowAPR: '0',
    stableBorrowAPY: '0',
    reserve,
    ...overrides,
  }
}

export function getMockAaveFormattedUserSummary(
  userReservesData: AaveUserSummary['userReservesData'],
  overrides: Partial<AaveUserSummary> = {},
): AaveUserSummary {
  return {
    userReservesData,
    totalLiquidityMarketReferenceCurrency: '0',
    totalLiquidityUSD: '0',
    totalCollateralMarketReferenceCurrency: '0',
    totalCollateralUSD: '0',
    totalBorrowsMarketReferenceCurrency: '0',
    totalBorrowsUSD: '0',
    netWorthUSD: '0',
    availableBorrowsMarketReferenceCurrency: '0',
    availableBorrowsUSD: '0',
    currentLoanToValue: '0',
    currentLiquidationThreshold: '0',
    healthFactor: '-1',
    isInIsolationMode: false,
    ...overrides,
  }
}

export const usdcLikeReserve = {
  ...daiLikeReserve,
  token: new Token({
    symbol: TokenSymbol('USDC'),
    name: 'USD Coin',
    decimals: 6,
    address: CheckedAddress('0xa0b86991c6218b36c1d19d4a2e9eb0ce3606eb48'),
    unitPriceUsd: '1',
  }),
}

export const usdtLikeReserve = {
  ...daiLikeReserve,
  token: new Token({
    symbol: TokenSymbol('USDT'),
    name: 'Tether USD',
    decimals: 6,
    address: CheckedAddress('0xdac17f958d2ee523a2206206994597c13d831ec7'),
    unitPriceUsd: '1',
  }),
}

export const sDaiLikeReserve = getMockReserve({
  token: new Token({
    symbol: TokenSymbol('sDAI'),
    name: 'Dai Stablecoin',
    decimals: 18,
    address: CheckedAddress('0x83F20F44975D03b1b09e64809B757c47f942BEeA'),
    unitPriceUsd: '1.1',
  }),
  status: 'active',
  supplyAvailabilityStatus: 'yes',
  collateralEligibilityStatus: 'no',
  borrowEligibilityStatus: 'no',
  isIsolated: false,
  isBorrowableInIsolation: true,
  eModeCategory: undefined,
  isSiloedBorrowing: false,
  availableLiquidity: NormalizedUnitNumber('24662247.809387867477416918'),
  availableLiquidityUSD: NormalizedUnitNumber('24662247.809387867477416918'),
  totalLiquidity: NormalizedUnitNumber('1022367834.197032800423989866'),
  totalLiquidityUSD: NormalizedUnitNumber('1022367834.197032800423989866'),
  totalDebt: NormalizedUnitNumber('997705586.387644932946572948'),
  totalDebtUSD: NormalizedUnitNumber('997705586.387644932946572948'),
  totalVariableDebt: NormalizedUnitNumber('997705586.387644932946572948'),
  totalVariableDebtUSD: NormalizedUnitNumber('997705586.387644932946572948'),
  isolationModeTotalDebt: NormalizedUnitNumber('0'),
  debtCeiling: NormalizedUnitNumber('0'),
  supplyAPY: Percentage('0.06299360148523566431'),
  maxLtv: Percentage('0'),
  variableBorrowApy: Percentage('0.06459999999999999996'),
  lastUpdateTimestamp: 1708334846,
  variableBorrowIndex: new BigNumber('1.035736103938278845101422738e+27'),
  variableBorrowRate: new BigNumber('6.2599141818649791361008e+25'),
  liquidityIndex: new BigNumber('1.028914737353923264312907136e+27'),
  liquidityRate: new BigNumber('6.1089080101931682768749404e+25'),
  priceInUSD: NormalizedUnitNumber('1'),
  usageAsCollateralEnabled: false,
  usageAsCollateralEnabledOnUser: false,
  incentives: { deposit: [], borrow: [] },
})

export const wethLikeReserve = getMockReserve({
  token: new Token({
    symbol: TokenSymbol('WHYPE'),
    name: 'Wrapped HYPE',
    decimals: 18,
    address: CheckedAddress('0x4eDF296e33e7f00DD5f14fC8640e1b70f3672B4C'),
    unitPriceUsd: '2907.40835',
  }),
  status: 'active',
  supplyAvailabilityStatus: 'yes',
  collateralEligibilityStatus: 'yes',
  borrowEligibilityStatus: 'yes',
  isIsolated: false,
  eModeCategory: undefined,
  isSiloedBorrowing: false,
  availableLiquidity: NormalizedUnitNumber('125501.896724995390811222'),
  availableLiquidityUSD: NormalizedUnitNumber('364885262.4790892529560601165'),
  totalLiquidity: NormalizedUnitNumber('240005.463747054418237119'),
  totalLiquidityUSD: NormalizedUnitNumber('697793889.34380830348699206054'),
  totalDebt: NormalizedUnitNumber('114503.567022059027425897'),
  totalDebtUSD: NormalizedUnitNumber('332908626.86471905053093194404'),
  totalVariableDebt: NormalizedUnitNumber('114503.567022059027425897'),
  totalVariableDebtUSD: NormalizedUnitNumber('332908626.86471905053093194404'),
  isolationModeTotalDebt: NormalizedUnitNumber('0'),
  debtCeiling: NormalizedUnitNumber('0'),
  supplyAPY: Percentage('0.00771786450868526367'),
  maxLtv: Percentage('0.8'),
  variableBorrowApy: Percentage('0.01710779075945549687'),
  lastUpdateTimestamp: 1708334829,
  variableBorrowIndex: new BigNumber('1.024848053360502593632035917e+27'),
  variableBorrowRate: new BigNumber('1.6963100401904001627543239e+25'),
  liquidityIndex: new BigNumber('1.013900866792617715471863134e+27'),
  liquidityRate: new BigNumber('7.688234151079366400606947e+24'),
  priceInUSD: NormalizedUnitNumber('2907.40835'),
  usageAsCollateralEnabled: true,
  usageAsCollateralEnabledOnUser: true,
  incentives: {
    deposit: [
      {
        token: new Token({
          symbol: TokenSymbol('wstHYPE'),
          name: 'Wrapped liquid staked Ether 2.0',
          decimals: 18,
          address: CheckedAddress('0x7f39C581F595B53c5cb19bD0b3f8dA6c935E2Ca0'),
          unitPriceUsd: '3365.40595559',
        }),
        APR: Percentage('0'),
      },
    ],
    borrow: [],
  },
})

export const testTokens = {
  DAI: daiLikeReserve.token,
  XDAI: daiLikeReserve.token.clone({
    name: 'XDAI',
    symbol: TokenSymbol('XDAI'),
  }),
  sDAI: sDaiLikeReserve.token,
  USDC: usdcLikeReserve.token,
  USDT: usdtLikeReserve.token,
  WETH: wethLikeReserve.token,
  USDS: new Token({
    address: CheckedAddress('0x798f111c92E38F102931F34D1e0ea7e671BDBE31'),
    symbol: TokenSymbol('USDS'),
    name: 'USDS',
    decimals: 18,
    unitPriceUsd: '1',
  }),
  sUSDS: new Token({
    address: CheckedAddress('0xeA8AE08513f8230cAA8d031D28cB4Ac8CE720c68'),
    symbol: TokenSymbol('sUSDS'),
    name: 'sUSDS',
    decimals: 18,
    unitPriceUsd: '1.05',
  }),
  token1: new Token({
    address: testAddresses.token,
    symbol: TokenSymbol('TKN1'),
    name: 'Token 1',
    decimals: 18,
    unitPriceUsd: '1',
  }),
  token2: new Token({
    address: testAddresses.token2,
    symbol: TokenSymbol('TKN2'),
    name: 'Token 2',
    decimals: 18,
    unitPriceUsd: '1',
  }),
  token3: new Token({
    address: testAddresses.token3,
    symbol: TokenSymbol('TKN3'),
    name: 'Token 3',
    decimals: 18,
    unitPriceUsd: '1',
  }),
  token4: new Token({
    address: testAddresses.token3,
    symbol: TokenSymbol('TKN4'),
    name: 'Token 4',
    decimals: 18,
    unitPriceUsd: '1',
  }),
}
